/* eslint-disable global-require, no-underscore-dangle, no-param-reassign */
import React from 'react'
import { createRoot } from 'react-dom/client'
import axios from 'axios'
import { ApolloProvider } from '@apollo/client'
import { config as configureApiBuilder } from '@hixme/api'
// project
import client from 'apollo/client'
import appConfig from 'modules/app-config'
import createStore from 'store/createStore'
import AppContainer from 'containers/AppContainer'
import { selectors, config as authConfig, restoreLoginState, refreshAuth } from '@hixme/auth-ui'
import { config as configureEpsAuthUi } from '@surecompanies/auth-ui'
import 'styles/core.scss'
import { initializeLDClient } from 'helpers/launchDarklyClient'
import { setupShpIdentitySync } from 'helpers/shopHealthSession'
import { setupEpsSessionSync, whenEpsSessionReady } from 'helpers/epsSession'
import { setupPausedStateRefresh } from 'helpers/pausedStateRefresh'
import { isImpersonating, getUserId } from 'store/modules/user-session/selectors'

const stage = appConfig.getStage()

// auth setup
authConfig({
  stage, // auth module follows stage of app
  refreshMilliseconds: 15 * 60 * 1000, // 45 minutes
})
configureEpsAuthUi({ stage })

// ========================================================
// Store Instantiation
// ========================================================
const initialState = window.__INITIAL_STATE__
let store = {}
const getAuthToken = () => ({
  Authorization: selectors.getToken(store.getState()),
})
const api = configureApiBuilder({ stage, getAuthToken })
store = createStore(initialState, { api })

// Restore and refresh the user's auth status
store.dispatch(restoreLoginState())
store.dispatch(refreshAuth())
setupShpIdentitySync(store, { whenEpsSessionReady })
// Establish/refresh the EPS session cookie while logged in; clear it on logout.
setupEpsSessionSync(store, { getCredentials: selectors.getCredentials, isImpersonating, getUserId })
// Keep the enrollment paused signal fresh on tab focus/visibility (no polling).
setupPausedStateRefresh(store)

// Listen to each Amazon API Gateway request
// axios is the http module used inside of the sdk
axios.interceptors.request.use(
  (config) => {
    // Add Authorization header to each API Gateway request
    const Authorization = selectors.getCredentials(store.getState() || {}).idToken
    config.headers.Authorization = Authorization || ''
    return config
  },
  (error) => Promise.reject(error)
)

// ========================================================
// Developer Tools Setup
// ========================================================
// if (__DEBUG__) {
//   if (window.devToolsExtension) {
//     window.devToolsExtension.open()
//   }
// }

// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById('root')
const root = createRoot(MOUNT_NODE)
let render = async () => {
  const routes = require('./routes/index').default(store)

  // initialize LD Client for no react hook LD calls
  // I.E. in mapDispatchToProps etc
  try {
    await initializeLDClient()
  } catch (e) {
    console.error(e)
  }

  root.render(
    <ApolloProvider client={client}>
      <AppContainer store={store} routes={routes} />
    </ApolloProvider>
  )
}

// This code is excluded from production bundle
if (__DEV__) {
  if (module.hot) {
    // Development render functions
    const renderApp = render
    const renderError = (error) => {
      const RedBox = require('redbox-react').default

      root.render(<RedBox error={error} />)
    }

    // Wrap render in try/catch
    render = () => {
      try {
        renderApp()
      } catch (error) {
        renderError(error)
      }
    }

    // Setup hot module replacement
    module.hot.accept('./routes/index', () => {
      setTimeout(() => {
        root.unmount()
        render()
      })
    })
  }
}

// ========================================================
// Go!
// ========================================================
render()
